import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiError } from '@/lib/api-client';
import {
  applyServerFieldErrors,
  makeCreateAdminUserFormSchema,
  type CreateAdminUserFormValues,
} from '@/lib/user-validation';
import type { AdminUser, UpdateAdminUserInput } from '@forest/contracts';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { Dialog } from '@/components/ui/Dialog';
import { FormField } from '@/components/ui/FormField';
import { PasswordRules } from '@/components/PasswordRules';
import { Table, Th, Td } from '@/components/ui/Table';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/sessionStore';
import { Link } from 'react-router-dom';
import { entitateBareKey } from '@/features/regions/entitateKey';

const MAX_ENTITIES_PER_USER = 20;

const ROLES = ['SUPER_ADMIN', 'CONTROLOR', 'ADMINISTRATOR', 'AJUTOR_ADMINISTRATOR'] as const;

const ROLE_COLORS: Record<string, 'blue' | 'green' | 'yellow' | 'gray'> = {
  SUPER_ADMIN: 'blue',
  CONTROLOR: 'green',
  ADMINISTRATOR: 'yellow',
  AJUTOR_ADMINISTRATOR: 'gray',
};

type GroupRow = { user: AdminUser; depth: number };
type UserGroup = { id: string; label: string; count: number; rows: GroupRow[] };

function byDisplayName(a: AdminUser, b: AdminUser): number {
  return (a.fullName || a.username).localeCompare(b.fullName || b.username, 'ro');
}

/**
 * Group users for the admin table: SUPER_ADMIN, then CONTROLOR, then each
 * ADMINISTRATOR immediately followed by their own AJUTOR_ADMINISTRATOR
 * users (depth 1, rendered indented), and finally any AJUTOR whose manager
 * isn't in the visible set ("unassigned"). Empty groups are omitted.
 */
function buildUserGroups(
  users: AdminUser[],
  label: (key: string, def: string) => string,
  selfId?: string,
): UserGroup[] {
  const ofRole = (r: string) => users.filter((u) => u.role === r);
  const supers = ofRole('SUPER_ADMIN').sort(byDisplayName);
  const controlors = ofRole('CONTROLOR').sort(byDisplayName);
  const admins = ofRole('ADMINISTRATOR').sort(byDisplayName);
  const ajutors = ofRole('AJUTOR_ADMINISTRATOR');
  const adminIds = new Set(admins.map((a) => a.id));
  const groups: UserGroup[] = [];

  if (supers.length)
    groups.push({
      id: 'super',
      label: label('users.group.superAdmins', 'Super administratori'),
      count: supers.length,
      rows: supers.map((u) => ({ user: u, depth: 0 })),
    });
  if (controlors.length)
    groups.push({
      id: 'controlor',
      label: label('users.group.controlors', 'Controlori'),
      count: controlors.length,
      rows: controlors.map((u) => ({ user: u, depth: 0 })),
    });

  const adminRows: GroupRow[] = [];
  let nested = 0;
  for (const admin of admins) {
    adminRows.push({ user: admin, depth: 0 });
    const kids = ajutors.filter((a) => a.managerId === admin.id).sort(byDisplayName);
    nested += kids.length;
    for (const k of kids) adminRows.push({ user: k, depth: 1 });
  }
  if (adminRows.length)
    groups.push({
      id: 'admins',
      label: label('users.group.administrators', 'Administratori și ajutoare'),
      count: admins.length + nested,
      rows: adminRows,
    });

  const orphans = ajutors
    .filter((a) => !a.managerId || !adminIds.has(a.managerId))
    .sort(byDisplayName);
  if (orphans.length) {
    // When an ADMINISTRATOR views their own AJUTORs, their manager (the
    // viewer) isn't in the list — label that group "My assistants" rather
    // than the misleading "without administrator".
    const allMine = !!selfId && orphans.every((a) => a.managerId === selfId);
    groups.push({
      id: 'orphans',
      label: allMine
        ? label('users.group.myAssistants', 'Ajutoarele mele')
        : label('users.group.unassignedAjutor', 'Ajutoare fără administrator'),
      count: orphans.length,
      rows: orphans.map((u) => ({ user: u, depth: 0 })),
    });
  }

  return groups;
}

export function UsersPage() {
  const { t } = useTranslation(['admin', 'common']);
  const currentUser = useAuthStore((s) => s.user);
  const isSuperAdmin = currentUser?.role === 'SUPER_ADMIN';
  const isAdministrator = currentUser?.role === 'ADMINISTRATOR';
  const [q, setQ] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  // Fetch the full user set (scale is ~100 users) so filtering by active
  // state and grouping by manager/role can happen entirely client-side —
  // a manager-grouped view can't be server-paginated coherently. The
  // server caps `limit` at 200, so page through it (≈1 request at this
  // scale) rather than asking for an over-cap page in one shot.
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', 'list'],
    queryFn: async () => {
      const items: AdminUser[] = [];
      let pageNo = 1;
      let totalPages = 1;
      do {
        const res = await api
          .get('admin/users', { searchParams: { page: pageNo, limit: 200 } })
          .json<{ items: AdminUser[]; pagination: { totalPages: number } }>();
        items.push(...res.items);
        totalPages = res.pagination.totalPages;
        pageNo += 1;
      } while (pageNo <= totalPages);
      return { items };
    },
  });

  // For SUPER_ADMIN: fetch distinct forestry entity options
  const { data: entityOptions } = useQuery({
    queryKey: ['me', 'entitate-options'],
    queryFn: () => api.get('me/entitate-options').json<string[]>(),
    enabled: isSuperAdmin,
  });

  // For SUPER_ADMIN: fetch all ADMINISTRATOR users for manager dropdown
  const { data: admins } = useQuery({
    queryKey: ['admin', 'administrators'],
    queryFn: () =>
      api
        .get('admin/users', { searchParams: { role: 'ADMINISTRATOR', limit: 200 } })
        .json<{ items: AdminUser[] }>(),
    enabled: isSuperAdmin,
  });

  const updateUser = useMutation({
    mutationFn: ({ id, data: body }: { id: string; data: UpdateAdminUserInput }) =>
      api.patch(`admin/users/${id}`, { json: body }).json<AdminUser>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin'] });
      pushToast({ variant: 'success', message: t('users.updateSuccess') });
    },
    onError: async (e) => {
      const err = await apiError(e);
      // EntitiesCell handles ENTITY_NOT_IN_SCOPE locally so it can roll back
      // optimistic state and tailor the message; skip the generic toast.
      if (err.code === 'ENTITY_NOT_IN_SCOPE') return;
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const resetPassword = useMutation({
    mutationFn: (id: string) =>
      api.post(`admin/users/${id}/reset-password`).json<{ tempPassword: string }>(),
    onSuccess: (data) => {
      pushToast({
        variant: 'info',
        message: `${t('users.passwordReset')}: ${data.tempPassword}`,
      });
    },
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  // Client-side filter (search + active state) then group.
  const allUsers = data?.items ?? [];
  const term = q.trim().toLowerCase();
  const visibleUsers = useMemo(
    () =>
      allUsers.filter((u) => {
        if (!showInactive && !u.isActive) return false;
        if (!term) return true;
        return (
          u.username.toLowerCase().includes(term) ||
          (u.fullName ?? '').toLowerCase().includes(term) ||
          (u.cod ?? '').toLowerCase().includes(term)
        );
      }),
    [allUsers, term, showInactive],
  );
  const groups = useMemo(
    () =>
      buildUserGroups(
        visibleUsers,
        (k, def) => t(k, { defaultValue: def }),
        currentUser?.id,
      ),
    [visibleUsers, t, currentUser?.id],
  );
  const inactiveCount = useMemo(
    () => allUsers.filter((u) => !u.isActive).length,
    [allUsers],
  );

  // Roles the current user can assign
  const assignableRoles = isSuperAdmin
    ? ROLES
    : (['AJUTOR_ADMINISTRATOR'] as const);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t('users.title')}</h1>
          <p className="text-sm text-slate-500">{t('users.subtitle')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          + {t('users.create')}
        </Button>
      </div>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-4">
            <Input
              type="search"
              placeholder={t('users.search')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="max-w-xs"
            />
            <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-forest-600 focus:ring-forest-500"
              />
              {t('users.showInactive', { defaultValue: 'Afișează inactivi' })}
              {inactiveCount > 0 ? (
                <span className="rounded-full bg-slate-100 px-1.5 text-xs text-slate-500 dark:bg-slate-700 dark:text-slate-400">
                  {inactiveCount}
                </span>
              ) : null}
            </label>
          </div>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && !data ? (
            <div className="flex justify-center p-10">
              <Spinner />
            </div>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>username</Th>
                  <Th>name</Th>
                  <Th>{t('users.cod', { defaultValue: 'Cod' })}</Th>
                  <Th>{t('users.role')}</Th>
                  <Th>{t('users.manager')}</Th>
                  <Th>{t('users.entitateSilvica')}</Th>
                  <Th>{t('users.active')}</Th>
                  <Th>{t('users.lastLogin')}</Th>
                  <Th>{t('common:common.actions')}</Th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr>
                    <Td colSpan={9} className="py-8 text-center text-slate-400">
                      {t('common:common.noResults', { defaultValue: 'Niciun rezultat' })}
                    </Td>
                  </tr>
                ) : (
                  groups.map((g) => (
                    <Fragment key={g.id}>
                      <tr className="bg-slate-50 dark:bg-slate-800/60">
                        <td
                          colSpan={9}
                          className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
                        >
                          {g.label}
                          <span className="ml-2 rounded-full bg-slate-200 px-1.5 text-[10px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                            {g.count}
                          </span>
                        </td>
                      </tr>
                      {g.rows.map(({ user: u, depth }) => (
                        <tr key={u.id} className={u.isActive ? undefined : 'opacity-50'}>
                          <Td className="font-mono text-xs">
                            <span
                              className="flex items-center gap-1"
                              style={depth ? { paddingLeft: depth * 18 } : undefined}
                            >
                              {depth > 0 ? (
                                <span className="text-slate-300 dark:text-slate-600">└</span>
                              ) : null}
                              <Link
                                to={`/app/admin/users/${u.id}`}
                                className="font-semibold text-forest-700 hover:underline dark:text-forest-300"
                                title={t('admin:users.viewDetails', { defaultValue: 'Vezi detalii' })}
                              >
                                {u.username}
                              </Link>
                            </span>
                          </Td>
                    <Td>{u.fullName}</Td>
                    <Td className="font-mono text-xs">
                      {isSuperAdmin ? (
                        <input
                          type="text"
                          defaultValue={u.cod ?? ''}
                          placeholder="—"
                          maxLength={32}
                          className="w-28 rounded border border-slate-200 bg-white px-2 py-1 text-xs focus:border-forest-500 focus:outline-none"
                          onBlur={(e) => {
                            const next = e.target.value.trim() || null;
                            if ((u.cod ?? null) !== next) {
                              updateUser.mutate({ id: u.id, data: { cod: next } });
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          }}
                        />
                      ) : (
                        <span className="text-slate-500">{u.cod ?? '—'}</span>
                      )}
                    </Td>
                    <Td>
                      {isSuperAdmin ? (
                        <Select
                          value={u.role}
                          onChange={(e) =>
                            updateUser.mutate({
                              id: u.id,
                              data: { role: e.target.value as typeof ROLES[number] },
                            })
                          }
                          className="text-xs"
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </Select>
                      ) : (
                        <Badge tone={ROLE_COLORS[u.role] ?? 'gray'}>{u.role}</Badge>
                      )}
                    </Td>
                    <Td className="text-xs text-slate-500">
                      {isSuperAdmin && u.role === 'AJUTOR_ADMINISTRATOR' ? (
                        <Select
                          value={u.managerId ?? ''}
                          onChange={(e) =>
                            updateUser.mutate({
                              id: u.id,
                              data: { managerId: e.target.value || null },
                            })
                          }
                          className="text-xs"
                        >
                          <option value="">—</option>
                          {admins?.items.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.fullName} ({a.username})
                            </option>
                          ))}
                        </Select>
                      ) : u.managerId ? (
                        <span className="text-slate-400">{u.managerId.slice(0, 8)}...</span>
                      ) : (
                        '—'
                      )}
                    </Td>
                    <Td className="text-xs text-slate-500">
                      <EntitiesCell
                        user={u}
                        canEdit={isSuperAdmin || isAdministrator}
                        isSuperAdmin={isSuperAdmin}
                        callerEntities={currentUser?.assignedEntitatiSilvice ?? []}
                        entityOptions={entityOptions ?? []}
                        onSave={(next) =>
                          updateUser.mutateAsync({
                            id: u.id,
                            data: { assignedEntitatiSilvice: next },
                          })
                        }
                      />
                    </Td>
                    <Td>
                      <Badge tone={u.isActive ? 'green' : 'red'}>
                        {u.isActive ? t('users.active') : t('users.inactive')}
                      </Badge>
                    </Td>
                    <Td className="text-xs text-slate-500">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}
                    </Td>
                    <Td>
                      <div className="flex flex-wrap gap-1">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            updateUser.mutate({
                              id: u.id,
                              data: { isActive: !u.isActive },
                            })
                          }
                        >
                          {u.isActive ? t('users.deactivate') : t('users.reactivate')}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetPassword.mutate(u.id)}
                          loading={resetPassword.isPending}
                        >
                          {t('users.resetPw')}
                        </Button>
                      </div>
                    </Td>
                        </tr>
                      ))}
                    </Fragment>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
        <div className="border-t border-slate-200 px-3 py-2 text-sm text-slate-500 dark:border-slate-700">
          {visibleUsers.length} {t('common:common.rows')}
          {!showInactive && inactiveCount > 0 ? (
            <span className="ml-1 text-slate-400">
              ({inactiveCount} {t('users.inactive').toLowerCase()})
            </span>
          ) : null}
        </div>
      </Card>

      <CreateUserDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        assignableRoles={assignableRoles}
        isAdministrator={isAdministrator}
        admins={admins?.items ?? []}
        isSuperAdmin={isSuperAdmin}
        callerEntities={currentUser?.assignedEntitatiSilvice ?? []}
      />
    </div>
  );
}

type EntitiesCellProps = {
  user: AdminUser;
  canEdit: boolean;
  isSuperAdmin: boolean;
  callerEntities: readonly string[];
  entityOptions: readonly string[];
  onSave: (next: string[]) => Promise<unknown>;
};

/**
 * Multi-value entitate silvica cell on the admin users table.
 * SUPER_ADMIN: free text + datalist suggestions from /api/me/entitate-options.
 * ADMINISTRATOR: select-only from caller's own assignedEntitatiSilvice.
 * Reorder via up/down arrow buttons (keyboard a11y) and HTML5 DnD.
 * The first pill carries an "Implicită" badge when list.length >= 2.
 */
function EntitiesCell({
  user,
  canEdit,
  isSuperAdmin,
  callerEntities,
  entityOptions,
  onSave,
}: EntitiesCellProps) {
  const { t } = useTranslation(['admin', 'common']);
  const pushToast = useUiStore((s) => s.pushToast);
  const baseList = user.assignedEntitatiSilvice ?? [];
  // Optimistic mirror; reset whenever the server-confirmed list changes.
  const [list, setList] = useState<string[]>(baseList);
  const [adding, setAdding] = useState(false);
  const [draftValue, setDraftValue] = useState('');
  const dragIndexRef = useRef<number | null>(null);
  const datalistId = `entitati-silvice-${user.id}`;

  useEffect(() => {
    setList(user.assignedEntitatiSilvice ?? []);
  }, [user.assignedEntitatiSilvice]);

  const commit = async (next: string[]) => {
    const before = list;
    setList(next);
    try {
      await onSave(next);
    } catch (e) {
      const err = await apiError(e);
      // Roll back optimistic state on failure.
      setList(before);
      if (err.code === 'ENTITY_NOT_IN_SCOPE') {
        const offenders = err.entitiesOutOfScope ?? [];
        pushToast({
          variant: 'error',
          message: t('users.entitatiSilvice.outOfScope', {
            entities: offenders.join(', '),
          }),
        });
      } else {
        pushToast({ variant: 'error', message: err.message });
      }
    }
  };

  const handleRemove = (idx: number) => {
    if (!canEdit) return;
    const next = list.filter((_, i) => i !== idx);
    void commit(next);
  };

  const handleMove = (idx: number, dir: -1 | 1) => {
    if (!canEdit) return;
    const target = idx + dir;
    if (target < 0 || target >= list.length) return;
    const next = [...list];
    const [moved] = next.splice(idx, 1);
    next.splice(target, 0, moved!);
    void commit(next);
  };

  const handleDragStart = (idx: number) => () => {
    dragIndexRef.current = idx;
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };
  const handleDrop = (idx: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    dragIndexRef.current = null;
    if (from === null || from === idx) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(idx, 0, moved!);
    void commit(next);
  };

  const handleAddSubmit = () => {
    const value = draftValue.trim();
    if (!value) return;
    // Reject a value that is the SAME entity as one already assigned,
    // even under a different prefix/spelling ("IS Chișinău" vs
    // "ÎSÎS Chișinău"). The server collapses these on save, so adding
    // both would report success yet store only one — surface it here.
    const key = entitateBareKey(value);
    if (list.some((e) => entitateBareKey(e) === key)) {
      pushToast({
        variant: 'error',
        message: t('users.entitatiSilvice.duplicate', {
          defaultValue: 'Această entitate silvică este deja atribuită.',
        }),
      });
      setDraftValue('');
      setAdding(false);
      return;
    }
    if (list.length >= MAX_ENTITIES_PER_USER) {
      pushToast({ variant: 'error', message: t('users.entitatiSilvice.tooMany') });
      return;
    }
    const next = [...list, value];
    setDraftValue('');
    setAdding(false);
    void commit(next);
  };

  const isAtCap = list.length >= MAX_ENTITIES_PER_USER;

  // Options that can be picked. SUPER_ADMIN: full distinct list from API.
  // ADMINISTRATOR: only their own list (cannot create new entries this way).
  // Deduped by bare key and with anything already assigned (under any
  // spelling) removed, so the same entity can't be offered/picked twice.
  const selectedKeys = new Set(list.map(entitateBareKey));
  const pickableOptions = (() => {
    const source = isSuperAdmin ? entityOptions ?? [] : callerEntities;
    const seen = new Set<string>();
    const out: string[] = [];
    for (const opt of source) {
      const key = entitateBareKey(opt);
      if (selectedKeys.has(key) || seen.has(key)) continue;
      seen.add(key);
      out.push(opt);
    }
    return out;
  })();

  return (
    <div className="flex flex-col gap-1">
      {list.length === 0 ? (
        <span className="text-slate-400">{t('users.entitatiSilvice.empty')}</span>
      ) : (
        <div className="flex flex-wrap gap-1">
          {list.map((entity, idx) => (
            <span
              key={`${entity}-${idx}`}
              draggable={canEdit}
              onDragStart={handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(idx)}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs"
            >
              <span className="font-medium text-slate-700">{entity}</span>
              {idx === 0 && list.length >= 2 && (
                <span className="rounded bg-forest-100 px-1 text-[10px] text-forest-700">
                  {t('users.entitatiSilvice.primary')}
                </span>
              )}
              {canEdit && (
                <>
                  <button
                    type="button"
                    aria-label={t('users.entitatiSilvice.moveUp')}
                    title={t('users.entitatiSilvice.moveUp')}
                    onClick={() => handleMove(idx, -1)}
                    disabled={idx === 0}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    aria-label={t('users.entitatiSilvice.moveDown')}
                    title={t('users.entitatiSilvice.moveDown')}
                    onClick={() => handleMove(idx, 1)}
                    disabled={idx === list.length - 1}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    aria-label={t('users.entitatiSilvice.remove')}
                    title={t('users.entitatiSilvice.remove')}
                    onClick={() => handleRemove(idx)}
                    className="text-slate-400 hover:text-red-600"
                  >
                    ×
                  </button>
                </>
              )}
            </span>
          ))}
        </div>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-1">
          {adding ? (
            isSuperAdmin ? (
              <>
                <input
                  type="text"
                  list={datalistId}
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  placeholder={t('users.entitatiSilvice.addPlaceholder')}
                  maxLength={100}
                  autoFocus
                  className="w-44 rounded border border-slate-300 px-2 py-0.5 text-xs focus:border-forest-500 focus:outline-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSubmit();
                    } else if (e.key === 'Escape') {
                      setDraftValue('');
                      setAdding(false);
                    }
                  }}
                />
                <datalist id={datalistId}>
                  {pickableOptions.map((opt) => (
                    <option key={opt} value={opt} />
                  ))}
                </datalist>
                <Button size="sm" variant="secondary" onClick={handleAddSubmit}>
                  {t('users.entitatiSilvice.confirmAdd')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraftValue('');
                    setAdding(false);
                  }}
                >
                  {t('users.entitatiSilvice.cancel')}
                </Button>
              </>
            ) : (
              <>
                <Select
                  value={draftValue}
                  onChange={(e) => setDraftValue(e.target.value)}
                  className="text-xs"
                >
                  <option value="">— {t('users.entitatiSilvice.add')} —</option>
                  {pickableOptions
                    .filter((opt) => !list.includes(opt))
                    .map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                </Select>
                <Button size="sm" variant="secondary" onClick={handleAddSubmit}>
                  {t('users.entitatiSilvice.confirmAdd')}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDraftValue('');
                    setAdding(false);
                  }}
                >
                  {t('users.entitatiSilvice.cancel')}
                </Button>
              </>
            )
          ) : (
            <Button
              size="sm"
              variant="ghost"
              disabled={isAtCap}
              title={isAtCap ? t('users.entitatiSilvice.tooMany') : undefined}
              onClick={() => setAdding(true)}
            >
              + {t('users.entitatiSilvice.add')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function CreateUserDialog({
  open,
  onClose,
  assignableRoles,
  isAdministrator,
  admins,
  isSuperAdmin,
  callerEntities,
}: {
  open: boolean;
  onClose: () => void;
  assignableRoles: readonly string[];
  isAdministrator: boolean;
  admins: AdminUser[];
  isSuperAdmin: boolean;
  /**
   * The caller's own `assignedEntitatiSilvice` list. Shown read-only
   * in the dialog so the operator knows what data the new user will
   * inherit visibility for (per the broadened `parcelScope` /
   * `photoScope` in v0.9.5: AJUTOR sees their manager's entities;
   * ADMINISTRATOR creating a peer doesn't apply since admins can
   * only create AJUTORs).
   */
  callerEntities: readonly string[];
}) {
  const { t } = useTranslation(['admin', 'common']);
  const qc = useQueryClient();
  const pushToast = useUiStore((s) => s.pushToast);

  // Rebuilt on language change so field errors re-localize on the next
  // validation pass.
  const resolver = useMemo(() => zodResolver(makeCreateAdminUserFormSchema(t)), [t]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    getValues,
    formState: { errors },
  } = useForm<CreateAdminUserFormValues>({
    resolver,
    defaultValues: {
      username: '',
      password: '',
      name: '',
      role: isAdministrator ? 'AJUTOR_ADMINISTRATOR' : 'CONTROLOR',
      preferredLanguage: 'ro',
      managerId: '',
    },
  });

  const selectedRole = watch('role');

  const createUser = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post('admin/users', { json: data }).json<AdminUser>(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin'] });
      pushToast({ variant: 'success', message: t('users.createSuccess') });
      reset();
      onClose();
    },
    onError: async (e) => {
      const err = await apiError(e);
      const handled = applyServerFieldErrors({
        err,
        setError,
        t,
        fields: ['username', 'name', 'password', 'role', 'preferredLanguage', 'managerId'] as const,
        getValue: (f) => getValues(f as 'password'),
      });
      if (handled) return;
      pushToast({
        variant: 'error',
        message: err.code === 'VALIDATION_FAILED' ? t('common:validation.checkFields') : err.message,
      });
    },
  });

  const onSubmit = (values: CreateAdminUserFormValues) => {
    createUser.mutate({
      username: values.username,
      password: values.password,
      name: values.name,
      role: values.role,
      preferredLanguage: values.preferredLanguage,
      managerId: values.managerId || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} title={t('users.createTitle')} size="md">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" noValidate>
        <FormField
          label={t('users.username')}
          htmlFor="cu-username"
          error={errors.username?.message}
          hint={t('common:validation.username.hint')}
          required
        >
          <Input id="cu-username" {...register('username')} />
        </FormField>
        <FormField
          label={t('users.name')}
          htmlFor="cu-name"
          error={errors.name?.message}
          hint={t('common:validation.name.hint')}
          required
        >
          <Input id="cu-name" {...register('name')} />
        </FormField>
        <FormField label={t('users.password')} htmlFor="cu-pw" error={errors.password?.message} required>
          <Input id="cu-pw" type="password" {...register('password')} />
          <PasswordRules value={watch('password') ?? ''} />
        </FormField>
        <FormField label={t('users.role')} htmlFor="cu-role">
          <Select id="cu-role" {...register('role')} disabled={isAdministrator}>
            {assignableRoles.map((r) => (
              <option key={r} value={r}>
                {t(`users.roleValue.${r}`, { defaultValue: r })}
              </option>
            ))}
          </Select>
        </FormField>
        {isAdministrator ? (
          <div
            role="note"
            className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs dark:border-slate-700 dark:bg-slate-800/60"
          >
            <p className="font-semibold text-slate-700 dark:text-slate-200">
              {t('users.entitatiSilvice.inheritedTitle')}
            </p>
            {callerEntities.length === 0 ? (
              <p className="mt-1 text-amber-700 dark:text-amber-300">
                {t('users.entitatiSilvice.inheritedNone')}
              </p>
            ) : (
              <>
                <p className="mt-1 text-slate-600 dark:text-slate-400">
                  {t('users.entitatiSilvice.inheritedHint')}
                </p>
                <ul className="mt-1.5 flex flex-wrap gap-1">
                  {callerEntities.map((e) => (
                    <li
                      key={e}
                      className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                    >
                      {e}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ) : null}
        {isSuperAdmin && selectedRole === 'AJUTOR_ADMINISTRATOR' && (
          <FormField label={t('users.manager')} htmlFor="cu-manager">
            <Select id="cu-manager" {...register('managerId')}>
              <option value="">— {t('users.noManager')} —</option>
              {admins.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.fullName} ({a.username})
                </option>
              ))}
            </Select>
          </FormField>
        )}
        <FormField label={t('users.language')} htmlFor="cu-lang">
          <Select id="cu-lang" {...register('preferredLanguage')}>
            <option value="ro">Română</option>
            <option value="ru">Русский</option>
            <option value="en">English</option>
          </Select>
        </FormField>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common:action.cancel')}
          </Button>
          <Button type="submit" loading={createUser.isPending}>
            {t('users.create')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
